import { useMemo, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppTextField } from "../form";
import type { NormalizedTablePayload } from "../../utils/tablePayload";
import { getTableCellDisplay } from "../../utils/tablePayload";
import { computeColumnNumericRange } from "../../utils/tableBarFill";
import {
  filterTableRows,
  nextTableSortState,
  sortTableRows,
  type TableSortState,
} from "../../utils/tableViewUtils";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import TableBarFillCell from "./TableBarFillCell";

type Props = {
  data: NormalizedTablePayload;
  fitContainer?: boolean;
};

export default function InteractiveTableView({ data, fitContainer = false }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.interactiveTableView;
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<TableSortState>(null);

  const columnRanges = useMemo(() => {
    const ranges = new Map<string, ReturnType<typeof computeColumnNumericRange>>();
    for (const col of data.columns) {
      if (!col.barFill) continue;
      const field = col.field || col.header;
      ranges.set(field, computeColumnNumericRange(data.rows, field, col.header, col.barFill));
    }
    return ranges;
  }, [data.columns, data.rows]);

  const visibleRows = useMemo(() => {
    const filtered = filterTableRows(data.rows, data.columns, filter);
    return sortTableRows(filtered, data.columns, sort);
  }, [data.columns, data.rows, filter, sort]);

  const hasSubtotals = Boolean(data.subtotals && Object.keys(data.subtotals).length);

  const toggleSort = (field: string) => {
    setSort((current) => nextTableSortState(current, field));
  };

  const renderCellContent = (
    row: Record<string, unknown>,
    col: NormalizedTablePayload["columns"][number]
  ) => {
    const field = col.field || col.header;
    const display = getTableCellDisplay(row, field, col.header, col.format);
    if (col.barFill) {
      const raw = row[field] ?? row[col.header];
      return (
        <TableBarFillCell
          value={raw}
          display={display}
          barFill={col.barFill}
          range={columnRanges.get(field) ?? null}
          format={col.format}
        />
      );
    }
    return display || "—";
  };

  return (
    <Box sx={styles.root(fitContainer)}>
      <Box sx={styles.filterBar} data-export-hide className="no-print">
        <AppTextField
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("widgetTable.filterPlaceholder")}
          hiddenLabel
          label={t("widgetTable.filterPlaceholder")}
          margin="none"
          sx={styles.filterField(fitContainer)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={styles.rowCountCaption}>
          {t("widgetTable.rowCount", { shown: visibleRows.length, total: data.rows.length })}
        </Typography>
      </Box>

      <TableContainer sx={styles.tableContainer(fitContainer)}>
        <Table size="small" stickyHeader sx={styles.table}>
          <TableHead>
            <TableRow>
              {data.columns.map((col, colIndex) => {
                const field = col.field || col.header;
                const active = sort?.field === field;
                return (
                  <TableCell
                    key={`${field}-${colIndex}`}
                    sortDirection={active ? sort.direction : false}
                    sx={styles.headerCell}
                  >
                    <TableSortLabel
                      active={active}
                      direction={active ? sort.direction : "asc"}
                      onClick={() => toggleSort(field)}
                    >
                      {col.header}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(data.columns.length, 1)} align="center" sx={styles.emptyRowCell}>
                  {filter.trim() ? t("widgetTable.noFilterResults") : t("dashboard.noWidgetData")}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  {data.columns.map((col, colIndex) => {
                    const field = col.field || col.header;
                    const title = col.barFill?.showValue === false
                      ? getTableCellDisplay(row, field, col.header, col.format)
                      : getTableCellDisplay(row, field, col.header, col.format);
                    return (
                      <TableCell
                        key={`${field}-${colIndex}`}
                        sx={col.barFill ? styles.barFillCell : styles.bodyCell}
                        title={title}
                      >
                        {renderCellContent(row, col)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
          {hasSubtotals && (
            <TableFooter>
              <TableRow sx={styles.footerRow}>
                {data.columns.map((col, colIndex) => {
                  const field = col.field || col.header;
                  const total = data.subtotals?.[field];
                  const text =
                    total != null
                      ? getTableCellDisplay({ [field]: total }, field, col.header, col.format)
                      : colIndex === 0
                        ? t("widgetTable.subtotalLabel")
                        : "";
                  return (
                    <TableCell key={`subtotal-${field}-${colIndex}`} sx={styles.footerCell}>
                      <strong>{text}</strong>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}
